import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import './components/ui/Toast.css';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import Dashboard from './components/dashboard/Dashboard';
import CalendarView from './components/calendar/CalendarView';
import SuperAdminPanel from './components/admin/SuperAdminPanel';
import PrivateRoute from './components/auth/PrivateRoute';
import './App.css';

// Component to handle redirect after Google sign-in
function RedirectHandler() {
  const { currentUser, processingRedirect } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is authenticated and on login/signup page, navigate to redirect path or dashboard
    if (currentUser && !processingRedirect && (location.pathname === '/login' || location.pathname === '/signup')) {
      // Check URL params first (most reliable), then sessionStorage
      const urlParams = new URLSearchParams(location.search);
      const urlRedirect = urlParams.get('redirect');
      const sessionRedirect = sessionStorage.getItem('redirectAfterLogin');

      // Clear sessionStorage
      sessionStorage.removeItem('redirectAfterLogin');

      // Use URL param if available, otherwise sessionStorage, otherwise dashboard
      const destination = urlRedirect ? decodeURIComponent(urlRedirect) : (sessionRedirect || '/dashboard');
      navigate(destination, { replace: true });
    }
  }, [currentUser, location.pathname, location.search, navigate, processingRedirect]);

  // Show loading spinner during redirect processing
  if (processingRedirect && typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(103, 126, 234, 0.95)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div className="spinner" style={{ margin: '0 auto', width: '40px', height: '40px', border: '4px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p style={{ marginTop: '20px', fontSize: '16px' }}>Completing sign-in...</p>
      </div>
    );
  }

  return null;
}

function App() {
  useEffect(() => {
    fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
      .then(r => r.json())
      .then(({ v }) => {
        const stored = localStorage.getItem('app_v');
        if (stored && stored !== v) {
          localStorage.setItem('app_v', v);
          window.location.reload();
        } else {
          localStorage.setItem('app_v', v);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <ThemeProvider>
      <ToastProvider>
      <AuthProvider>
        <Router>
          <RedirectHandler />
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              
              {/* Calendar View - Public (guests can view) */}
              <Route path="/calendar/:id" element={<CalendarView />} />

              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />

              {/* Super Admin Panel */}
              <Route path="/admin" element={
                <PrivateRoute requiredRole="superAdmin">
                  <SuperAdminPanel />
                </PrivateRoute>
              } />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/login" />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;